import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip,
  LineChart, Line,
} from "recharts";
import { motion } from "framer-motion";
import { TrendUp, HourglassMedium, CheckCircle, Coins, ChartLineUp, Warning, ShieldWarning, MicrosoftExcelLogo, Download, ClipboardText, CircleNotch, FloppyDisk, CheckSquare, Circle, WarningCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function downloadDashboardExport(fmt) {
  const token = localStorage.getItem("access_token") || "";
  try {
    const res = await fetch(`${API_BASE}/exports/dashboard/${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const nm = /filename="?([^"]+)"?/.exec(cd)?.[1] || (fmt === "xlsx" ? "dashboard.xlsx" : "dashboard.zip");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = nm; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${nm}`);
  } catch (e) {
    toast.error(`Export failed: ${e.message}`);
  }
}

async function downloadWeeklyBackup() {
  const token = localStorage.getItem("access_token") || "";
  try {
    const res = await fetch(`${API_BASE}/backup/xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const nm = /filename="?([^"]+)"?/.exec(cd)?.[1] || "casedist_weekly_backup.xlsx";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = nm; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${nm}`);
  } catch (e) {
    toast.error(`Backup download failed: ${e.message}`);
  }
}

const fmt = (n) => `₱${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const _P_TONE = {
  high: "border-rose-200 bg-rose-50 text-rose-800",
  medium: "border-amber-200 bg-amber-50 text-amber-800",
  low: "border-emerald-200 bg-emerald-50 text-emerald-800",
};

function TodoWidget({ todos, onChanged }) {
  const today = new Date().toISOString().slice(0, 10);
  // Sort: overdue first (by due_date asc), then high priority, then earliest due_date, then newest.
  const _PPRI = { high: 0, medium: 1, low: 2 };
  const sorted = [...todos].sort((a, b) => {
    const aOver = a.due_date && a.due_date < today ? 0 : 1;
    const bOver = b.due_date && b.due_date < today ? 0 : 1;
    if (aOver !== bOver) return aOver - bOver;
    const pa = _PPRI[a.priority] ?? 1;
    const pb = _PPRI[b.priority] ?? 1;
    if (pa !== pb) return pa - pb;
    const da = a.due_date || "9999";
    const db = b.due_date || "9999";
    if (da !== db) return da < db ? -1 : 1;
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
  const top = sorted.slice(0, 3);
  const overdueCount = todos.filter((t) => t.due_date && t.due_date < today).length;
  const remaining = Math.max(todos.length - top.length, 0);

  async function toggleDone(t) {
    try {
      await api.put(`/todos/${t.id}`, { done: true });
      toast.success("Marked done");
      onChanged?.();
    } catch { toast.error("Failed to update"); }
  }

  return (
    <div className="grid-tile" data-testid="dashboard-todo-widget">
      <div className="p-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <CheckSquare size={16} className="text-muted-foreground" />
          <div>
            <div className="section-caption">Private to you</div>
            <div className="font-heading font-bold tracking-tight">My To-Do — Top 3</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {overdueCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-rose-200 bg-rose-50 text-rose-800" data-testid="todo-widget-overdue">
              <WarningCircle size={12} weight="fill" /> {overdueCount} overdue
            </span>
          )}
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="todo-widget-open-count">
            {todos.length} open
          </span>
          <Link to="/todos" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="todo-widget-open-page">
            Open list →
          </Link>
        </div>
      </div>
      {top.length === 0 ? (
        <div className="p-6 text-sm text-muted-foreground italic flex items-center justify-between">
          <span>Nothing on your list — enjoy the calm.</span>
          <Link to="/todos" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border rounded px-2 py-1" data-testid="todo-widget-add-link">
            Add one
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {top.map((t) => {
            const overdue = t.due_date && t.due_date < today;
            return (
              <li key={t.id} className="p-4 flex items-center gap-3" data-testid={`todo-widget-row-${t.id}`}>
                <button
                  type="button"
                  onClick={() => toggleDone(t)}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  title="Mark done"
                  data-testid={`todo-widget-toggle-${t.id}`}
                >
                  <Circle size={18} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.title}</div>
                  <div className="mt-1 flex flex-wrap gap-2 items-center">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${_P_TONE[t.priority] || _P_TONE.medium}`}>
                      {t.priority}
                    </span>
                    {t.due_date && (
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${overdue ? "border-rose-200 bg-rose-50 text-rose-800" : "border-border bg-muted/40 text-muted-foreground"}`}>
                        Due {t.due_date}{overdue ? " · overdue" : ""}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
          {remaining > 0 && (
            <li className="p-3 text-center">
              <Link to="/todos" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="todo-widget-see-more">
                +{remaining} more →
              </Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function KpiTile({ label, value, hint, icon: Icon, tone = "default", testId }) {
  const toneCls = {
    default: "bg-card",
    dark: "bg-foreground text-background",
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`grid-tile p-5 ${toneCls}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-4">
        <span className={`kpi-label ${tone === "dark" ? "text-background/60" : ""}`}>{label}</span>
        {Icon && <Icon size={16} className={tone === "dark" ? "text-background/70" : "text-muted-foreground"} />}
      </div>
      <div className="kpi-value">{value}</div>
      {hint && <div className={`text-xs mt-2 ${tone === "dark" ? "text-background/60" : "text-muted-foreground"}`}>{hint}</div>}
    </motion.div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const seesCoverage = can(user, ["admin", "finance"]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coverage, setCoverage] = useState(null);
  const [coverageMonth, setCoverageMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [todos, setTodos] = useState([]);

  async function loadTodos() {
    try {
      const { data } = await api.get("/todos", { params: { include_done: false } });
      setTodos(data || []);
    } catch { setTodos([]); }
  }

  async function loadCoverage(month) {
    if (!seesCoverage) return;
    setCoverageLoading(true);
    try {
      const { data } = await api.get("/dashboard/report-coverage", { params: { month } });
      setCoverage(data);
    } catch { setCoverage(null); }
    finally { setCoverageLoading(false); }
  }

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, cov] = await Promise.all([
          api.get("/dashboard/stats"),
          seesCoverage ? api.get("/dashboard/report-coverage", { params: { month: coverageMonth } }).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        setStats(data);
        setCoverage(cov.data);
        loadTodos();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [seesCoverage]);

  useEffect(() => {
    if (!loading && seesCoverage) loadCoverage(coverageMonth);
    // eslint-disable-next-line
  }, [coverageMonth]);

  if (loading || !stats) {
    return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;
  }

  const statusData = [
    { name: "Open", budget: stats.budget.open, quotation: stats.quotation.open, po: stats.purchase_order.open, billing: stats.billing.open },
    { name: "In Progress", budget: stats.budget.in_progress, quotation: stats.quotation.in_progress, po: stats.purchase_order.in_progress, billing: stats.billing.in_progress },
    { name: "Completed", budget: stats.budget.completed, quotation: stats.quotation.completed, po: stats.purchase_order.completed, billing: stats.billing.completed },
  ];

  const financialsData = [
    { name: "Requested", value: stats.financials.total_requested },
    { name: "Approved", value: stats.financials.total_approved },
    { name: "PO Value", value: stats.financials.total_po_value },
    { name: "Paid", value: stats.financials.total_paid },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Negative cash balance alert */}
      {stats.cash_position?.negative && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-rose-400 bg-rose-50 text-rose-900 rounded-md p-4 flex items-start gap-3"
          data-testid="negative-balance-banner"
        >
          <ShieldWarning size={20} weight="fill" className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold tracking-tight text-sm">
              Cash position is negative — ₱{stats.cash_position.closing.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs mt-0.5 opacity-80">
              Carried opening ₱{stats.cash_position.opening.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
              {stats.cash_position.anchor_date ? ` from ${stats.cash_position.anchor_date} closing` : ""} · as of {stats.cash_position.as_of}. Record an Additional Fund to bring the balance back above zero.
            </div>
          </div>
          <Link to="/cash-position" className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border rounded hover:opacity-80 shrink-0" style={{ borderColor: "currentColor" }} data-testid="banner-cash-position">
            Cash Position
          </Link>
        </motion.div>
      )}
      {/* Expiring permits banner */}
      {stats.expiring_permits && stats.expiring_permits.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className={`border rounded-md p-4 flex items-start gap-3 ${stats.expiring_permits.some((p) => p.expired) ? "border-rose-300 bg-rose-50 text-rose-900" : "border-amber-300 bg-amber-50 text-amber-900"}`}
          data-testid="expiring-permits-banner"
        >
          <Warning size={20} weight="fill" className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold tracking-tight text-sm">
              {stats.expiring_permits.filter((p) => p.expired).length > 0
                ? `${stats.expiring_permits.filter((p) => p.expired).length} permit(s) expired · `
                : ""}
              {stats.expiring_permits.filter((p) => !p.expired).length} permit(s) expire within 30 days
            </div>
            <div className="text-xs mt-0.5 opacity-80 truncate">
              {stats.expiring_permits.slice(0, 3).map((p) => `${p.title} (${p.expired ? `expired ${Math.abs(p.days_left)}d ago` : `${p.days_left}d left`})`).join(" · ")}
              {stats.expiring_permits.length > 3 && ` · +${stats.expiring_permits.length - 3} more`}
            </div>
          </div>
          <Link to="/permits" className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border rounded hover:opacity-80 shrink-0" style={{ borderColor: "currentColor" }} data-testid="banner-review-permits">
            Review
          </Link>
        </motion.div>
      )}

      {/* Header */}
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Overview</div>
          <h1 className="section-title text-3xl sm:text-4xl">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Live snapshot of requests, approvals and financial flow.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" className="border-border" onClick={downloadWeeklyBackup} data-testid="download-backup-dashboard-btn">
              <FloppyDisk size={16} className="mr-2" weight="bold" />
              Download Backup
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-border" data-testid="export-dashboard-btn">
                <MicrosoftExcelLogo size={16} className="mr-2" weight="bold" />
                Export to Excel
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => downloadDashboardExport("xlsx")} data-testid="export-dashboard-xlsx">
                <MicrosoftExcelLogo size={14} className="mr-2" />
                Excel workbook (.xlsx)
                <span className="text-[10px] text-muted-foreground ml-auto">multi-sheet</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadDashboardExport("csv")} data-testid="export-dashboard-csv">
                <Download size={14} className="mr-2" />
                CSV (.zip)
                <span className="text-[10px] text-muted-foreground ml-auto">3 files</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiTile
          label="Pending Approvals"
          value={stats.pending.budget_approvals + stats.pending.quotation_approvals}
          hint={`${stats.pending.budget_approvals} budget · ${stats.pending.quotation_approvals} quotes`}
          icon={HourglassMedium}
          tone="dark"
          testId="kpi-pending-approvals"
        />
        <KpiTile label="Total Requested" value={fmt(stats.financials.total_requested)} hint="All budget submissions" icon={Coins} testId="kpi-total-requested" />
        <KpiTile label="Total Approved" value={fmt(stats.financials.total_approved)} hint="Cleared for procurement" icon={CheckCircle} testId="kpi-total-approved" />
        <KpiTile label="PO Value" value={fmt(stats.financials.total_po_value)} hint="Issued purchase orders" icon={TrendUp} testId="kpi-po-value" />
      </div>

      {/* Personal To-Do widget (private per user) */}
      <TodoWidget todos={todos} onChanged={loadTodos} />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="grid-tile p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="section-caption">By Status</div>
              <div className="font-heading font-bold text-lg tracking-tight">Workflow distribution</div>
            </div>
            <ChartLineUp size={18} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717A" }} stroke="#E4E4E7" />
                <YAxis tick={{ fontSize: 11, fill: "#71717A" }} stroke="#E4E4E7" />
                <Tooltip contentStyle={{ background: "#09090B", color: "#fff", border: "1px solid #27272A", fontSize: 12 }} />
                <Bar dataKey="budget" fill="#18181B" name="Budget" />
                <Bar dataKey="quotation" fill="#71717A" name="Quotation" />
                <Bar dataKey="po" fill="#3B82F6" name="PO" />
                <Bar dataKey="billing" fill="#10B981" name="Billing" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid-tile p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="section-caption">Financial Flow</div>
              <div className="font-heading font-bold text-lg tracking-tight">Requested → Approved → Paid</div>
            </div>
            <Coins size={18} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={financialsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717A" }} stroke="#E4E4E7" />
                <YAxis tick={{ fontSize: 11, fill: "#71717A" }} stroke="#E4E4E7" />
                <Tooltip
                  contentStyle={{ background: "#09090B", color: "#fff", border: "1px solid #27272A", fontSize: 12 }}
                  formatter={(v) => `$${v.toLocaleString()}`}
                />
                <Bar dataKey="value" fill="#18181B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Two-column: Recent Budgets + Top Suppliers */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
        <div className="grid-tile">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <div className="section-caption">Recent</div>
              <div className="font-heading font-bold tracking-tight">Latest Requests & Vouchers</div>
            </div>
          </div>
          <div className="divide-y divide-border">
            {stats.recent_budgets.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No requests yet.</div>
            ) : stats.recent_budgets.map((b) => (
              <div key={b.id} className="p-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{b.request_no}</div>
                  <div className="font-medium truncate">{b.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{b.department} · {b.submitted_by_name}</div>
                </div>
                <div className="text-right">
                  <div className="tabular-nums font-heading font-bold">{fmt(b.total_amount)}</div>
                  <div className="mt-1"><StatusBadge status={b.status} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid-tile">
          <div className="p-4 border-b border-border">
            <div className="section-caption">Performance</div>
            <div className="font-heading font-bold tracking-tight">Top Suppliers</div>
          </div>
          <div className="divide-y divide-border">
            {stats.top_suppliers.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No suppliers yet.</div>
            ) : stats.top_suppliers.map((s) => (
              <div key={s.id} className="p-4">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="tabular-nums font-heading font-bold">{s.rating?.toFixed(1) || "—"}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground uppercase tracking-widest">
                  <div>Delivery <span className="text-foreground tabular-nums">{s.on_time_delivery || 0}%</span></div>
                  <div>Quality <span className="text-foreground tabular-nums">{s.quality_score || 0}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Report coverage widget (Admin + Finance) */}
      {seesCoverage && coverage && (
        <div className="grid-tile" data-testid="report-coverage-widget">
          <div className="p-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="section-caption flex items-center gap-1.5"><ClipboardText size={12} /> Report Coverage</div>
              <div className="font-heading font-bold tracking-tight">
                {(() => {
                  const [y,m] = (coverage.month || "").split("-");
                  const lbl = (y && m) ? new Date(Number(y), Number(m)-1, 1).toLocaleString(undefined, { month: "long", year: "numeric" }) : coverage.month;
                  return `Client Reports — ${lbl}`;
                })()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="month"
                value={coverageMonth}
                onChange={(e) => setCoverageMonth(e.target.value)}
                className="border border-border rounded px-2 py-1 text-sm bg-card"
                data-testid="coverage-month-picker"
              />
              <button
                type="button"
                onClick={() => setCoverageMonth(new Date().toISOString().slice(0, 7))}
                className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border rounded px-2 py-1"
                data-testid="coverage-this-month"
                title="Jump to current month"
              >Today</button>
              <Link to="/client-reports" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">Open reports →</Link>
            </div>
          </div>
          {coverageLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading coverage…</div>
          ) : (
            <>
              {/* Trend sparkline (last 6 months coverage %) */}
              {coverage.trend && coverage.trend.length > 1 && (
                <div className="px-4 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Coverage % — last {coverage.trend.length} months</div>
                    <div className="text-[10px] font-heading font-bold tracking-tight tabular-nums" data-testid="coverage-latest-pct">
                      {coverage.trend[coverage.trend.length - 1].coverage_pct.toFixed(1)}%
                    </div>
                  </div>
                  <div style={{ width: "100%", height: 64 }} data-testid="coverage-sparkline">
                    <ResponsiveContainer>
                      <LineChart data={coverage.trend} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#71717A" }} tickFormatter={(m) => {
                          const [y, mm] = (m || "").split("-");
                          return y && mm ? new Date(Number(y), Number(mm) - 1, 1).toLocaleString(undefined, { month: "short" }) : m;
                        }} axisLine={false} tickLine={false} />
                        <YAxis hide domain={[0, 100]} />
                        <Tooltip
                          contentStyle={{ fontSize: 11, borderRadius: 4, border: "1px solid #E4E4E7" }}
                          formatter={(v) => [`${v}%`, "Coverage"]}
                          labelFormatter={(m) => {
                            const [y, mm] = (m || "").split("-");
                            return y && mm ? new Date(Number(y), Number(mm) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" }) : m;
                          }}
                        />
                        <Line type="monotone" dataKey="coverage_pct" stroke="#18181B" strokeWidth={2} dot={{ r: 3, fill: "#18181B" }} activeDot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              <div className="p-4 grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total clients</div>
                  <div className="font-heading font-black text-2xl tabular-nums">{coverage.total_clients}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">With report</div>
                  <div className="font-heading font-black text-2xl tabular-nums text-emerald-700">{coverage.with_report}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Missing</div>
                  <div className={`font-heading font-black text-2xl tabular-nums ${coverage.missing > 0 ? "text-rose-700" : "text-emerald-700"}`}>{coverage.missing}</div>
                </div>
              </div>
              {coverage.missing > 0 && (
                <div className="px-4 pb-4">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Still missing this month</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(coverage.missing_clients || []).map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1 border border-rose-200 bg-rose-50 text-rose-700 rounded px-2 py-0.5 text-xs" data-testid={`missing-client-${c.id}`}>
                        <CircleNotch size={10} weight="bold" /> {c.name}
                      </span>
                    ))}
                    {coverage.missing > (coverage.missing_clients || []).length && (
                      <span className="text-xs text-muted-foreground">+{coverage.missing - (coverage.missing_clients || []).length} more</span>
                    )}
                  </div>
                </div>
              )}
              {coverage.missing === 0 && coverage.total_clients > 0 && (
                <div className="px-4 pb-4 text-xs text-emerald-700 flex items-center gap-1.5">
                  <CheckCircle size={14} weight="fill" /> All clients covered this month.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Expiring permits widget */}
      {stats.expiring_permits && stats.expiring_permits.length > 0 && (
        <div className="grid-tile" data-testid="expiring-permits-widget">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <div className="section-caption flex items-center gap-1.5"><ShieldWarning size={12} /> Compliance</div>
              <div className="font-heading font-bold tracking-tight">Expiring / Expired Permits</div>
            </div>
            <Link to="/permits" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
              Open repository →
            </Link>
          </div>
          <div className="divide-y divide-border">
            {stats.expiring_permits.map((p) => {
              const tone = p.expired ? "text-rose-700 bg-rose-50 border-rose-200" : (p.days_left <= 7 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-zinc-700 bg-zinc-50 border-zinc-200");
              return (
                <div key={p.id} className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">{p.permit_type}</div>
                    <div className="font-medium truncate">{p.title}</div>
                    <div className="text-xs text-muted-foreground">Expires {p.expiry_date?.slice(0, 10) || "—"}</div>
                  </div>
                  <div className={`text-xs uppercase tracking-widest font-semibold border rounded px-2 py-1 whitespace-nowrap ${tone}`} data-testid={`expiry-tag-${p.id}`}>
                    {p.expired ? `Expired ${Math.abs(p.days_left)}d ago` : `${p.days_left}d left`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
